import * as React from "react"

import { cn } from "../../cn"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverItem,
  PopoverTitle,
} from "../Popover/Popover"
import { ResizeGrip, type ResizeGripOrientation } from "./ResizeGrip"

/** Pointer travel (px) below which a press on the separator counts as a tap, not a drag. */
const TAP_SLOP = 3

/** How many steps Shift with an arrow key moves at once. */
const LARGE_STEP_MULTIPLIER = 4

export type ResizeSeparatorProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "role" | "tabIndex" | "defaultValue"
> & {
  /** Name of the pane the separator resizes, e.g. "Sofia panel". */
  label: string
  /**
   * Which way the divider runs: a vertical divider sits between side-by-side
   * panes, a horizontal one between stacked panes.
   */
  orientation?: ResizeGripOrientation
  /**
   * Side of the divider the resized pane sits on: `before` is left of a
   * vertical divider or above a horizontal one, `after` is right or below.
   * It decides which arrow key grows the pane.
   */
  paneSide: "before" | "after"
  /** The pane's current size, in whatever unit the owner tracks. */
  value: number
  onValueChange: (value: number) => void
  /** How far one arrow key or one step control moves the divider. */
  step: number
  min?: number
  max?: number
  /** Size Reset returns to. Without one the controls offer no Reset. */
  defaultValue?: number
  /** Spoken form of the value, e.g. `480px` or `38%`. */
  formatValue?: (value: number) => string
  /** True while the owner is tracking a drag; keeps the grip showing. */
  isResizing?: boolean
}

/**
 * A focusable divider between two panes. Dragging it is left to the owner,
 * which passes its own pointer or mouse handlers through. The separator adds
 * the ways to resize without dragging: the arrow keys step the pane (Shift
 * steps further, Home and End go to the limits), and a tap or Enter opens
 * step and reset controls for pointer users who cannot drag (WCAG 2.5.7).
 *
 * The controls hang off an anchor rather than a trigger, because the
 * separator role does not allow the trigger's `aria-haspopup` /
 * `aria-expanded`.
 */
export const ResizeSeparator = React.forwardRef<
  HTMLDivElement,
  ResizeSeparatorProps
>(
  (
    {
      label,
      orientation = "vertical",
      paneSide,
      value,
      onValueChange,
      step,
      min,
      max,
      defaultValue,
      formatValue,
      isResizing = false,
      className,
      onPointerDown,
      onKeyDown,
      ...props
    },
    forwardedRef,
  ) => {
    const [open, setOpen] = React.useState(false)
    const separatorRef = React.useRef<HTMLDivElement | null>(null)
    const stopTapTrackingRef = React.useRef<(() => void) | null>(null)
    const titleId = React.useId()

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        separatorRef.current = node
        if (typeof forwardedRef === "function") {
          forwardedRef(node)
        } else if (forwardedRef) {
          forwardedRef.current = node
        }
      },
      [forwardedRef],
    )

    React.useEffect(() => () => stopTapTrackingRef.current?.(), [])

    const clampTo = (next: number, lo = min, hi = max) =>
      Math.min(hi ?? Infinity, Math.max(lo ?? -Infinity, next))
    // The reported range stretches to take in the current value: a size saved
    // under limits that have since moved (a narrower window) is still
    // reported as it is, and steps start from it rather than jumping.
    const rangeMin = min === undefined ? undefined : Math.min(min, value)
    const rangeMax = max === undefined ? undefined : Math.max(max, value)
    const resizeBy = (direction: 1 | -1, multiplier = 1) => {
      onValueChange(
        clampTo(value + direction * step * multiplier, rangeMin, rangeMax),
      )
    }
    // Reset lands where the owner would clamp the default to, so it is only
    // offered when it would move the divider.
    const resetValue =
      defaultValue === undefined ? undefined : clampTo(defaultValue)

    const growKey =
      orientation === "vertical"
        ? paneSide === "before"
          ? "ArrowRight"
          : "ArrowLeft"
        : paneSide === "before"
          ? "ArrowDown"
          : "ArrowUp"
    const shrinkKey = {
      ArrowRight: "ArrowLeft",
      ArrowLeft: "ArrowRight",
      ArrowDown: "ArrowUp",
      ArrowUp: "ArrowDown",
    }[growKey]

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event)
      if (event.defaultPrevented) {
        return
      }
      const multiplier = event.shiftKey ? LARGE_STEP_MULTIPLIER : 1
      let handled = true
      if (event.key === "Enter") {
        setOpen(true)
      } else if (event.key === growKey) {
        resizeBy(1, multiplier)
      } else if (event.key === shrinkKey) {
        resizeBy(-1, multiplier)
      } else if (event.key === "Home" && min !== undefined) {
        onValueChange(min)
      } else if (event.key === "End" && max !== undefined) {
        onValueChange(max)
      } else {
        handled = false
      }
      if (handled) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    // A press that never travels is a tap, and toggles the controls. The window
    // sees the release even when the owner's drag handling keeps the pointer
    // off the separator. Whether the controls were open is read at the press,
    // because the popover dismisses itself on that press.
    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
      onPointerDown?.(event)
      if (event.button !== 0) {
        return
      }
      stopTapTrackingRef.current?.()
      const { pointerId, clientX: startX, clientY: startY } = event
      const wasOpen = open
      let moved = false
      const handleMove = (move: PointerEvent) => {
        if (
          move.pointerId === pointerId &&
          Math.hypot(move.clientX - startX, move.clientY - startY) >= TAP_SLOP
        ) {
          moved = true
        }
      }
      const handleUp = (up: PointerEvent) => {
        if (up.pointerId !== pointerId) {
          return
        }
        stop()
        if (!moved) {
          setOpen(!wasOpen)
        }
      }
      const stop = () => {
        window.removeEventListener("pointermove", handleMove)
        window.removeEventListener("pointerup", handleUp)
        window.removeEventListener("pointercancel", stop)
        stopTapTrackingRef.current = null
      }
      window.addEventListener("pointermove", handleMove)
      window.addEventListener("pointerup", handleUp)
      window.addEventListener("pointercancel", stop)
      stopTapTrackingRef.current = stop
    }

    // A focusable separator is a widget under ARIA, so it takes the pointer
    // and key handlers directly.
    const separatorHandlers = {
      onPointerDown: handlePointerDown,
      onKeyDown: handleKeyDown,
    }

    const dimension = orientation === "vertical" ? "width" : "height"
    const atMin = min !== undefined && value <= min
    const atMax = max !== undefined && value >= max

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            ref={setRefs}
            role="separator"
            aria-orientation={orientation}
            aria-label={`Resize ${label}`}
            aria-valuenow={Math.round(value)}
            aria-valuemin={rangeMin}
            aria-valuemax={rangeMax}
            aria-valuetext={formatValue?.(value)}
            tabIndex={0}
            data-resizing={isResizing || open}
            {...props}
            {...separatorHandlers}
            className={cn("group", className)}
          >
            <ResizeGrip orientation={orientation} />
          </div>
        </PopoverAnchor>
        <PopoverContent
          aria-labelledby={titleId}
          align="center"
          side={orientation === "vertical" ? "right" : "bottom"}
          className="w-44 p-1"
          // Back to the separator, which has no trigger for Radix to return to.
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            separatorRef.current?.focus()
          }}
        >
          <PopoverTitle id={titleId}>
            {label.charAt(0).toUpperCase() + label.slice(1)} {dimension}
          </PopoverTitle>
          <PopoverItem disabled={atMax} onClick={() => resizeBy(1)}>
            {orientation === "vertical" ? "Wider" : "Taller"}
          </PopoverItem>
          <PopoverItem disabled={atMin} onClick={() => resizeBy(-1)}>
            {orientation === "vertical" ? "Narrower" : "Shorter"}
          </PopoverItem>
          {resetValue !== undefined && (
            <PopoverItem
              disabled={value === resetValue}
              onClick={() => onValueChange(resetValue)}
            >
              Reset {dimension}
            </PopoverItem>
          )}
        </PopoverContent>
      </Popover>
    )
  },
)

ResizeSeparator.displayName = "ResizeSeparator"
